import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = {
    class: "px-5 pt-6 pb-4 flex flex-col flex-1 min-h-0",
    "data-testid": "collecte-form-list-view"
};
const _hoisted_2 = { class: "flex items-center justify-between mb-6 sm:mb-8" };
const _hoisted_3 = {
    class: "text-2xl sm:text-3xl font-bold text-gray-900 shrink-0",
    "data-testid": "collecte-form-list-title"
};
const _hoisted_4 = {
    key: 0,
    class: "flex flex-col items-center justify-center py-24",
    "data-testid": "collecte-form-list-error"
};
const _hoisted_5 = { class: "text-sm text-gray-700 mb-4" };
import { ref, computed, h, onMounted, watch } from "vue";
import { RouterLink, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { Badge, Button, DataTable, Dropdown, Pager, TableShell, useTableQuery, useTableViews } from "@eudonet/opal";
import { handleNavigationFailure } from "@/shared/utils/navigation";
import { buildTableLabels } from "@/shared/composables/tableLabels";
import { useListRowSelection } from "@/shared/composables/useListRowSelection";
import ListFloatActionBar from "@/shared/components/ListFloatActionBar.vue";
import { formatDate } from "@/shared/utils/date-formatting";
import { useDonationFormStore } from "../../stores/useDonationFormStore";
import { useProfileStore } from "../../../settings/stores/useProfileStore";
import { useFormListColumnSettings } from "../composables/useFormListColumnSettings";
import { formStatusTheme, formStatusIcon } from "../composables/formStatusBadge";
import { useCollecteMetricFormat } from "../composables/useCollecteMetricFormat";
import { buildPublicFormUrl, isPublicFormLinkActive } from "../composables/publicFormUrl";
import { logger } from "@/infrastructure/services/LoggerService";
import { campaignsApi } from "@/modules/communications/infrastructure/api/campaigns-api";
import FormCreateModal from "../components/FormCreateModal.vue";
import FormDuplicateModal from "../components/FormDuplicateModal.vue";
import FormRenameModal from "../components/FormRenameModal.vue";
import FormArchiveModal from "../components/FormArchiveModal.vue";
const ACTIONS_COLUMN_CONTROLS = 3;
export default /*@__PURE__*/ _defineComponent({
    __name: 'FormListView',
    setup(__props) {
        const { t, locale } = useI18n();
        const { formatCount, formatMoney } = useCollecteMetricFormat();
        const router = useRouter();
        const store = useDonationFormStore();
        const profileStore = useProfileStore();
        const ROW_OPTIONS = [10, 30, 50];
        const showCreateModal = ref(false);
        // Duplicate flow: the per-row "⋯" menu's Duplicate item stores the chosen source row and
        // opens the modal. The modal always stays mounted (like FormCreateModal) and reads the current
        // source from `duplicateSource`; `openDuplicate` sets it BEFORE flipping `showDuplicateModal`, so the
        // modal's open-watcher seeds its fields from the freshly chosen row (id + slug + title — no extra GET,
        // the row already carries all five). `campaignId` + `status` are carried for the notice
        // only: together they settle the certain half of it — a live source IS its campaign's carrier, so
        // the copy cannot keep that campaign. An ARCHIVED source released it, and whether a successor has
        // since taken it is not on this row at all: the modal answers that half from the tenant-wide taken
        // set it primes on open. See `FormDuplicateModal.campaignWillBeLost` for both branches. The initial
        // `status` is the type's first member and is
        // never rendered: the modal is only ever opened through `openDuplicate`, which sets a real row
        // first, and the seed exists so the ref is typed rather than optional.
        const showDuplicateModal = ref(false);
        const duplicateSource = ref({
            id: "",
            publicSlug: "",
            title: "",
            campaignId: null,
            status: "Draft",
        });
        function openDuplicate(row) {
            duplicateSource.value = {
                id: row.id,
                publicSlug: row.publicSlug,
                title: row.title,
                campaignId: row.campaignId,
                status: row.status,
            };
            showDuplicateModal.value = true;
        }
        // Rename flow: same always-mounted-modal shape as Duplicate above. `openRename` stores the chosen
        // row BEFORE flipping the flag so the modal's open-watcher seeds its field from the freshly chosen
        // row. Only the TITLE is editable — the public slug is immutable, so the row carries everything the
        // modal needs and no extra GET is required. The store patches the list row from the response, so
        // there is no `renamed` event to listen for here.
        const showRenameModal = ref(false);
        const renameTarget = ref({ id: "", title: "" });
        function openRename(row) {
            renameTarget.value = { id: row.id, title: row.title };
            showRenameModal.value = true;
        }
        // Archive flow: same always-mounted-modal shape as the duplicate one above — `openArchive` sets the
        // chosen row BEFORE flipping `showArchiveModal`, so the modal's open-watcher resets its fields and
        // collects the redirect candidates for the freshly chosen form. The title falls back to the slug
        // because `title` may be blank (the Titre column already renders a dash for it) and the dialog names
        // the form being archived.
        const showArchiveModal = ref(false);
        const archiveSource = ref({ id: "", title: "" });
        function openArchive(row) {
            archiveSource.value = { id: row.id, title: row.title || row.publicSlug };
            showArchiveModal.value = true;
        }
        // The states the backend accepts an archive from (`DonationForm::archive()` guards on exactly this
        // set; Draft and already-Archived are a 409) — R42: the menu offers no action the backend refuses.
        const ARCHIVABLE_STATUSES = ["Published", "Paused"];
        // Per-row "⋯" actions. Kept as a builder so a further action slots in without touching the column
        // render. Order: Rename, Duplicate, Archive.
        //
        // Rename is omitted entirely — not rendered `disabled: true` — on an Archived row: the backend
        // refuses a rename on Archived with 409 Form.NotEditable (R42), and OPAL's DropdownItem `disabled`
        // lands on a native <button> (DropdownItemButton) while Dropdown exposes only `trigger` / `menu`
        // slots and no PER-ITEM slot. So there is nowhere to hang an explaining Tooltip and the disabled
        // button cannot take focus: a disabled entry would be keyboard/AT-unreachable with no reason shown,
        // and R74's remedy is structurally unavailable here. The guard is a DENY-list on the one refused
        // status, never an allow-list on the accepted ones: the backend accepts Draft, Published AND Paused,
        // and an allow-list would silently drop the action on whichever status a future author forgets. A
        // stale row (archived by someone else since the page loaded) still reaches the 409, which the modal
        // maps to a localized message.
        //
        // Duplicate is offered for EVERY status, Archived included: the backend deep-copies an archived form
        // without complaint, and keeping a retired form re-usable as a template is the product decision
        // behind archiving being definitive in the first place.
        //
        // Archive is the list's ONLY lifecycle action and it is TERMINAL. Pausing (reversible unpublish) and
        // renaming the public SLUG are deliberately absent here — the product decision is that a retired
        // campaign is duplicated into a new form rather than edited back into service, which keeps its
        // statistics clean and its published links honest. (Rename above edits the admin TITLE only; the
        // slug a form publishes under stays immutable.)
        function rowActions(row) {
            const items = [];
            if (row.status !== "Archived") {
                items.push({
                    // `ri-edit-line`, not `ri-pencil-line`: the row's Edit button already owns the pencil.
                    label: t("collecte.list.rename.action"),
                    icon: "ri-edit-line",
                    testId: `collecte-form-rename-${row.id}`,
                    onClick: () => openRename(row),
                });
            }
            items.push({
                label: t("collecte.forms.duplicate.action"),
                icon: "ri-file-copy-line",
                testId: `collecte-form-duplicate-${row.id}`,
                onClick: () => openDuplicate(row),
            });
            if (ARCHIVABLE_STATUSES.includes(row.status)) {
                items.push({
                    label: t("collecte.forms.archive.action"),
                    icon: "ri-archive-line",
                    // The design system's destructive-item signal, as on every other irreversible portal action.
                    // Without it an unreplayable archive reads exactly like the benign Duplicate above it.
                    danger: true,
                    testId: `collecte-form-archive-${row.id}`,
                    onClick: () => openArchive(row),
                });
            }
            return items;
        }
        // useTableQuery drives the shared chrome; the list itself is server-paginated through the store.
        const tableQuery = useTableQuery({ pageSize: ROW_OPTIONS[0] });
        const tableViews = useTableViews({ initialViews: [] });
        const { columnSettings } = useFormListColumnSettings();
        // ─── Campaign id → name cache ──────────────────────────────────────────────────
        // The list rows carry only `campaignId` (a GUID); the "Campagne" column must show the
        // friendly campaign NAME instead. We resolve each distinct id by fetching the campaign
        // directly by id (`GET /campaigns/{id}`) — same proven pattern as CampaignSection. NOTE:
        // do NOT use the campaigns-list `search` param to look up by id — OPINK's
        // ListCampaignsQueryHandler matches `search` on Name only, so a list-search by the
        // campaignId always returns an empty page and the name can never resolve.
        //
        // The cache accumulates across pages so re-pagination never refetches an already-resolved
        // id. The column cell reads `campaignNames.value[id]`, so once an async resolution lands in
        // this reactive map the cell re-renders to the name; before/if it fails it falls back to the
        // GUID. Best-effort per id — a failed lookup (e.g. 404 for a stale campaign) logs (R47) and
        // leaves that id unresolved so the GUID shows.
        const campaignNames = ref({});
        async function resolveCampaignNames(forms) {
            const pending = [
                ...new Set(forms
                    .map((f) => f.campaignId)
                    .filter((id) => !!id && !(id in campaignNames.value))),
            ];
            if (pending.length === 0)
                return;
            await Promise.allSettled(pending.map(async (id) => {
                try {
                    const campaign = await campaignsApi.getCampaignById(id);
                    if (campaign.name) {
                        campaignNames.value = { ...campaignNames.value, [id]: campaign.name };
                    }
                }
                catch (err) {
                    // Leave this id unresolved (the GUID stays visible); R47 — silent catch logs.
                    logger.error("[FormListView] Failed to resolve campaign name", err);
                }
            }));
        }
        // Resolve names whenever the loaded rows change (initial load, retry, pagination, page-size
        // change). The cache de-dupes, so a page revisit is a no-op.
        watch(() => store.forms, (forms) => {
            void resolveCampaignNames(forms);
        }, { immediate: true });
        // Column order + pinning flow from the settings composable so the drawer's reorder/pin
        // actions drive the table (mirrors CampaignListView). Neither "select" nor "actions" is a
        // settings item (kept out of useFormListColumnSettings): the row-checkbox column leads the
        // order and the left-pinned cluster, the "actions" column is appended and right-pinned, so
        // the settings drawer shows no toggle for either and they never reorder.
        const columnOrder = computed(() => ["select", ...columnSettings.order.value, "actions"]);
        const columnPinning = computed(() => ({
            left: ["select", ...(columnSettings.pinning.value.left ?? []).filter((k) => k !== "select")],
            right: [...(columnSettings.pinning.value.right ?? []).filter((k) => k !== "actions"), "actions"],
        }));
        // The store captures the failure in `store.error` (which drives the error state below),
        // so swallow the rejection here to avoid an unhandled promise rejection on mount; log it
        // for diagnostics (R47 — silent catch with no separate toast).
        async function loadForms() {
            try {
                await store.loadForms({ page: store.currentPage, perPage: store.perPage });
            }
            catch (err) {
                logger.error("[FormListView] Failed to load donation forms", err);
            }
        }
        function onPageChange(page) {
            store.loadForms({ page, perPage: store.perPage }).catch((err) => {
                logger.error("[FormListView] Failed to change page", err);
            });
        }
        function onPageSizeChange(size) {
            store.loadForms({ page: 1, perPage: size }).catch((err) => {
                logger.error("[FormListView] Failed to change page size", err);
            });
        }
        // Row "edit" affordance. Same destination as the slug link in the publicSlug column — the
        // wizard's first section — but reachable as an explicit icon. Kept as its own function rather
        // than folded into onFormCreated so each navigation keeps its own diagnostic tag, exactly like
        // onPageChange / onPageSizeChange above.
        function openEditor(id) {
            router
                .push({ name: "CollecteFormCampaign", params: { id } })
                .catch(handleNavigationFailure("FormListView:edit"));
        }
        // Row "statistics" affordance — the read-only report at /collecte/forms/:id/statistics. Its own
        // function, and its own diagnostic tag, exactly like openEditor above.
        //
        // NO status gating, and that is a verified decision rather than an omission: the upstream dashboard
        // handler never reads `status`, 404s only an unknown id, and returns zeroed financial totals for a
        // campaign-less Draft — so all four statuses (Draft / Published / Paused / Archived) are servable
        // and every row's report is real information. A disabled state would also have to carry an R74
        // focusable-tooltip wrapper to explain itself to keyboard/AT users, for no benefit.
        function openStatistics(id) {
            router
                .push({ name: "CollecteFormStatistics", params: { id } })
                .catch(handleNavigationFailure("FormListView:statistics"));
        }
        function onFormCreated(id) {
            // Navigate straight to the new form's detail/wizard (first section) so the manager keeps
            // configuring it without a round-trip through the list.
            router
                .push({ name: "CollecteFormCampaign", params: { id } })
                .catch(handleNavigationFailure("FormListView:created"));
        }
        onMounted(loadForms);
        const tablePermissions = {
            canShareView: false,
        };
        const tableLabels = computed(() => buildTableLabels(t));
        // ── Row selection + float action bar ─────────────────────────────────────────
        const { selectColumn, effectiveCount, allPagesSelected, totalAcrossAllPages, clearSelection, onSelectAllPages } = useListRowSelection({
            t,
            rows: () => store.forms,
            getRowId: (form) => form.id,
            totalCount: () => store.totalItems,
        });
        // Trailing actions column, sized to its exact content box: 24 (p-3 both sides) + n * 32 (xs
        // iconOnly buttons) + (n - 1) * 4 (gap-1). The cell is `noTruncate` (overflow-visible), so an
        // under-sized width spills into the neighbouring column instead of clipping.
        const ACTIONS_COLUMN_WIDTH = 24 + ACTIONS_COLUMN_CONTROLS * 32 + (ACTIONS_COLUMN_CONTROLS - 1) * 4;
        const formColumns = [
            {
                key: "publicSlug",
                header: t("collecte.forms.columns.slug"),
                cell: ({ row }) => {
                    const slug = row.getValue("publicSlug") || "–";
                    const id = row.original.id;
                    return h(RouterLink, {
                        to: { name: "CollecteFormCampaign", params: { id } },
                        class: "text-blue-500 hover:underline",
                        "data-testid": `collecte-form-row-link-${id}`,
                    }, () => slug);
                },
                draggable: true,
            },
            {
                key: "title",
                header: t("collecte.forms.columns.title"),
                cell: ({ row }) => row.getValue("title") || "–",
                draggable: true,
            },
            {
                key: "campaignId",
                header: t("collecte.forms.columns.campaign"),
                // Show the resolved campaign name; fall back to the raw GUID while it resolves (or if the
                // lookup fails), then to a dash. Reading `campaignNames.value` here ties the cell to the
                // reactive cache so it re-renders once the async name lands.
                cell: ({ row }) => {
                    const id = row.getValue("campaignId");
                    return (id && campaignNames.value[id]) ?? id ?? "–";
                },
                draggable: true,
            },
            {
                key: "status",
                header: t("collecte.forms.columns.status"),
                cell: ({ row }) => {
                    const status = row.getValue("status");
                    return h(Badge, {
                        label: t(`collecte.forms.status.${status}`),
                        theme: formStatusTheme[status],
                        showCloseIcon: false,
                        size: "sm",
                        leftIcon: formStatusIcon[status],
                    });
                },
                draggable: true,
            },
            {
                key: "defaultCurrency",
                header: t("collecte.forms.columns.currency"),
                cell: ({ row }) => row.getValue("defaultCurrency") || "–",
                draggable: true,
            },
            {
                key: "donationCount",
                header: t("collecte.forms.columns.donations"),
                // Grouping matters: a lifetime count reaches 4-6 digits, and "12,345" / "12 345" beats "12345".
                // The finite guard and the dash live in `useCollecteMetricFormat`.
                cell: ({ row }) => formatCount(row.getValue("donationCount")),
                draggable: true,
            },
            {
                key: "totalAmountMinor",
                header: t("collecte.forms.columns.collectedAmount"),
                // `row.original.currency`, NOT `row.getValue("currency")`: OPAL derives its TanStack accessors
                // only from declared column keys, and `currency` is a RENDERING INPUT rather than a column of its
                // own, so `getValue("currency")` resolves to undefined.
                // `currency` WINS over `defaultCurrency` whenever it is well-formed: the total is aggregated from
                // the campaign's validated donations and carries the currency it was summed in, which may differ.
                // The shape and finite guards, and the dash they fall through to, live in `useCollecteMetricFormat`.
                cell: ({ row }) => formatMoney(row.getValue("totalAmountMinor"), row.original.currency || row.original.defaultCurrency),
                draggable: true,
            },
            {
                key: "createdAt",
                header: t("collecte.forms.columns.createdAt"),
                // Audit stamp: date only, no clock (mirrors the campaigns list — a creation date in a list has
                // no user-relevant time-of-day). `formatDate` returns the same "–" the sibling cells fall back
                // to when the value is absent, so no extra guard is needed here. The profile format is read
                // INSIDE the cell so the column re-renders once the async profile lands — R30. Hoisting it to a
                // setup-time const would freeze `undefined` into formatDate's "DD/MM/YYYY" default.
                cell: ({ row }) => formatDate(row.getValue("createdAt"), locale.value, profileStore.profile?.dateFormat),
                draggable: true,
            },
            {
                // Public donor-page URL. Display-only, so it declares an `id` and not a `key`: there is no
                // `publicUrl` field on DonationFormSummary (DataTableColumn.key is `keyof T & string`) — the
                // value is derived from `publicSlug` + the runtime public base URL, so the cell reads
                // `row.original`, never `row.getValue`. Deliberately NOT `noTruncate`: DataTableCell already
                // truncates and sets a native title on overflow, which is exactly what a long URL wants.
                id: "publicUrl",
                header: t("collecte.forms.columns.publicUrl"),
                // Wider floor than the 130px default: the content is a full absolute URL, and at the default
                // width every row truncates to the same origin prefix, which would show nothing that tells
                // one form from another. Still truncates on a narrow viewport, where DataTableCell adds a
                // native title carrying the whole URL.
                minWidth: 240,
                cell: ({ row }) => {
                    const form = row.original;
                    const url = buildPublicFormUrl(form.publicSlug);
                    // No public site configured for this environment (or no slug): nothing to show and nothing
                    // to explain to the end user — fall back to the same dash the sibling cells use.
                    if (!url) {
                        return "–";
                    }
                    // Only a Published form has a version served publicly. The others still show the address
                    // they WILL have, as muted inert text: the acceptance criterion gates *activating* the link,
                    // not showing the URL, and the slug is fixed at creation so the value is real information.
                    // No `href` is rendered in that state, rather than a merely aria-disabled anchor — that
                    // would still be openable by middle-click and by the context menu.
                    //
                    // No tooltip explaining the inactive state, deliberately: the reason is already on the row,
                    // visible without hover, in the Status column. A tooltip here would instead collide with the
                    // native title DataTableCell puts on an overflowing cell, break the truncation ellipsis with
                    // its inline-flex wrapper, and add one tab stop per unpublished row.
                    if (!isPublicFormLinkActive(form.status)) {
                        return h("span", {
                            class: "text-(--colors-text-body-subtle)",
                            "data-testid": `collecte-form-public-url-inactive-${form.id}`,
                        }, url);
                    }
                    return h("a", {
                        href: url,
                        target: "_blank",
                        // `noopener` closes reverse-tabnabbing on the new-tab hop. `noreferrer` rides with it as
                        // every other external anchor in this app does, and keeps the admin's portal URL out of
                        // the public front's access logs.
                        rel: "noopener noreferrer",
                        class: "text-blue-500 hover:underline",
                        // Names the form, because an aria-label REPLACES the accessible name: a constant string
                        // would make every row's link announce identically, leaving a screen-reader user unable
                        // to tell which form they are about to open.
                        "aria-label": t("collecte.forms.publicUrl.openAria", { title: form.title }),
                        "data-testid": `collecte-form-public-url-${form.id}`,
                    }, url);
                },
                draggable: true,
            },
            {
                // Trailing per-row actions ("⋯") column. System column: right-pinned for layout (forced
                // last by the columnPinning computed), NOT draggable, NO pin affordance, and NOT a settings item
                // — so the column-settings drawer shows no toggle for it and it never reorders. `noTruncate` lets
                // the Dropdown menu overflow the cell. Mirrors this list's leading "select" column, on the right.
                //
                // Width is the exact content box (see ACTIONS_COLUMN_WIDTH): update the control count there
                // when an icon is added to or removed from the cell below.
                id: "actions",
                header: "",
                width: ACTIONS_COLUMN_WIDTH,
                minWidth: ACTIONS_COLUMN_WIDTH,
                maxWidth: ACTIONS_COLUMN_WIDTH,
                draggable: false,
                resizable: false,
                sortable: false,
                showPinIcon: false,
                noTruncate: true,
                cell: ({ row }) => {
                    const form = row.original;
                    // Edit first, statistics second, "⋯" last: the overflow menu stays the row's rightmost
                    // control (and its last tab stop), matching the rest of the portal, and the pencil keeps its
                    // x-offset so the new icon costs no muscle memory. Same variant/size on all three so they
                    // read as one cluster.
                    //
                    // NOTE for anyone comparing with communications' ActionsTimelineCard: that card renders the
                    // pencil and the chart as ALTERNATIVES (`v-if="editable"` / `v-else`), because a sent action
                    // is no longer editable and an unsent one has no statistics. Neither half holds here — a
                    // donation form stays editable and stays measurable on every status — so this cell shows BOTH,
                    // permanently. It is a deliberate divergence from that card, not an oversight.
                    return h("div", { class: "flex items-center gap-1" }, [
                        h(Button, {
                            variant: "text",
                            size: "xs",
                            iconOnly: true,
                            iconLeft: "ri-pencil-line",
                            "aria-label": t("collecte.forms.editAction"),
                            "data-testid": `collecte-form-edit-${form.id}`,
                            onClick: () => openEditor(form.id),
                        }),
                        h(Button, {
                            variant: "text",
                            size: "xs",
                            iconOnly: true,
                            iconLeft: "ri-bar-chart-2-line",
                            "aria-label": t("collecte.forms.statisticsAction"),
                            "data-testid": `collecte-form-statistics-${form.id}`,
                            onClick: () => openStatistics(form.id),
                        }),
                        h(Dropdown, {
                            items: rowActions(form),
                            ghost: true,
                            menuAlign: "right",
                            "aria-label": t("collecte.forms.actionsMenu"),
                            "data-testid": `collecte-form-actions-${form.id}`,
                        }, {
                            trigger: ({ triggerProps }) => h(Button, {
                                ...triggerProps,
                                variant: "text",
                                size: "xs",
                                iconOnly: true,
                                iconLeft: "ri-more-line",
                                "aria-label": t("collecte.forms.actionsMenu"),
                                "data-testid": `collecte-form-actions-trigger-${form.id}`,
                            }),
                        }),
                    ]);
                },
            },
        ];
        const columns = computed(() => [selectColumn, ...formColumns]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("h1", _hoisted_3, _toDisplayString(_ctx.$t("collecte.forms.title")), 1 /* TEXT */),
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        label: _ctx.$t('collecte.forms.create.cta'),
                        "icon-left": "ri-add-fill",
                        "data-testid": "collecte-form-list-create-button",
                        onClick: _cache[0] || (_cache[0] = ($event) => (showCreateModal.value = true))
                    }, null, 8 /* PROPS */, ["label"])
                ]),
                (_unref(store).error && !_unref(store).forms.length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                        _cache[5] || (_cache[5] = _createElementVNode("i", {
                            class: "ri-error-warning-line text-4xl text-red-400 mb-4",
                            "aria-hidden": "true"
                        }, null, -1 /* CACHED */)),
                        _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t("collecte.forms.loadError")), 1 /* TEXT */),
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _ctx.$t('collecte.forms.retry'),
                            "data-testid": "collecte-form-list-retry-button",
                            onClick: loadForms
                        }, null, 8 /* PROPS */, ["label"])
                    ]))
                    : (_openBlock(), _createBlock(_unref(TableShell), {
                        key: 1,
                        query: _unref(tableQuery),
                        views: _unref(tableViews),
                        labels: tableLabels.value,
                        permissions: tablePermissions,
                        "show-controls": true,
                        "show-view-selector": true,
                        "show-search": false,
                        "data-testid": "collecte-form-list-table-shell"
                    }, {
                        table: _withCtx(() => [
                            _createVNode(_unref(DataTable), {
                                columns: columns.value,
                                data: _unref(store).forms,
                                "has-settings": "",
                                "column-visibility": _unref(columnSettings).visibility,
                                "column-order": columnOrder.value,
                                "column-pinning": columnPinning.value,
                                "settings-aria-label": _ctx.$t('collecte.forms.columns.customize'),
                                loading: _unref(store).isLoading,
                                "sticky-header": "",
                                "empty-text": _ctx.$t('collecte.forms.empty'),
                                onSettingsClick: _unref(columnSettings).openDrawer,
                                "onUpdate:columnOrder": _unref(columnSettings).setOrder,
                                "onUpdate:columnPinning": _unref(columnSettings).setPinning
                            }, null, 8 /* PROPS */, ["columns", "data", "column-visibility", "column-order", "column-pinning", "settings-aria-label", "loading", "empty-text", "onSettingsClick", "onUpdate:columnOrder", "onUpdate:columnPinning"])
                        ]),
                        pagination: _withCtx(() => [
                            _createVNode(_unref(Pager), {
                                "current-page": _unref(store).currentPage,
                                "total-pages": _unref(store).totalPages,
                                "total-items": _unref(store).totalItems,
                                "items-per-page": _unref(store).perPage,
                                "rows-per-page-options": ROW_OPTIONS,
                                "rows-per-page-label": _ctx.$t('pagination.rowsPerPage'),
                                "items-count-separator": _ctx.$t('pagination.of'),
                                "previous-label": _ctx.$t('pagination.previous'),
                                "next-label": _ctx.$t('pagination.next'),
                                "go-to-page-label": _ctx.$t('pagination.goToPage'),
                                "go-label": _ctx.$t('pagination.go'),
                                "show-go-to-page": "",
                                "show-items-count": "",
                                onPageChange: onPageChange,
                                onItemsPerPageChange: onPageSizeChange
                            }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "rows-per-page-label", "items-count-separator", "previous-label", "next-label", "go-to-page-label", "go-label"])
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["query", "views", "labels"])),
                _createVNode(ListFloatActionBar, {
                    "selection-count": _unref(effectiveCount),
                    "all-pages-selected": _unref(allPagesSelected),
                    "total-across-all-pages": _unref(totalAcrossAllPages),
                    onDismiss: _unref(clearSelection),
                    onSelectAllPages: _unref(onSelectAllPages)
                }, null, 8 /* PROPS */, ["selection-count", "all-pages-selected", "total-across-all-pages", "onDismiss", "onSelectAllPages"]),
                _createVNode(FormCreateModal, {
                    modelValue: showCreateModal.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((showCreateModal).value = $event)),
                    onCreated: onFormCreated
                }, null, 8 /* PROPS */, ["modelValue"]),
                _createVNode(FormDuplicateModal, {
                    modelValue: showDuplicateModal.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((showDuplicateModal).value = $event)),
                    "source-id": duplicateSource.value.id,
                    "source-slug": duplicateSource.value.publicSlug,
                    "source-title": duplicateSource.value.title,
                    "source-campaign-id": duplicateSource.value.campaignId,
                    "source-status": duplicateSource.value.status,
                    onDuplicated: onFormCreated
                }, null, 8 /* PROPS */, ["modelValue", "source-id", "source-slug", "source-title", "source-campaign-id", "source-status"]),
                _createVNode(FormRenameModal, {
                    modelValue: showRenameModal.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((showRenameModal).value = $event)),
                    "form-id": renameTarget.value.id,
                    "current-title": renameTarget.value.title
                }, null, 8 /* PROPS */, ["modelValue", "form-id", "current-title"]),
                _createVNode(FormArchiveModal, {
                    modelValue: showArchiveModal.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((showArchiveModal).value = $event)),
                    "source-id": archiveSource.value.id,
                    "source-title": archiveSource.value.title
                }, null, 8 /* PROPS */, ["modelValue", "source-id", "source-title"])
            ]));
        };
    }
});
